import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AuthServiceUAU, SessionState, User, Empresa, LoginRequest } from '../services/authServiceUAU';
import { resetSessionState } from '../services/api';
import { toastError, toastSuccess } from '../utils/toastHelper';

// ===============================
// MAPEO UAU → CAMPING COMPATIBLE
// ===============================

interface UAUUserData {
  user_id?: number;
  username?: string;
  email?: string;
  full_name?: string;
  is_active?: boolean;
  user_type?: string;
  organizations?: Empresa[];
  permissions?: Record<string, boolean>;
}

const mapUAUToRendicionesUser = (uauData: UAUUserData): User => {
  // Convertir permisos planos a estructura esperada
  const permisos = Object.entries(uauData.permissions || {})
    .filter(([_, granted]) => granted)
    .map(([codigo, _]) => ({
      id: Math.random(), // ID temporal
      codigo,
      nombre: codigo,
      descripcion: `Permiso ${codigo}`,
      categoria: codigo.split('.')[0] || 'general'
    }));

  // Validar campos obligatorios
  const userId = uauData.user_id || 0;
  const username = uauData.username || '';
  const email = uauData.email || '';
  const fullName = uauData.full_name || username;
  const isActive = uauData.is_active !== false;
  const userType = uauData.user_type || 'operador';
  const organizations = uauData.organizations || [];

  return {
    id: userId,
    username: username,
    email: email,
    nombre_completo: fullName,
    es_activo: isActive,
    tipo_usuario: userType as 'operador' | 'supervisor' | 'administrador',
    auth_service_id: userId.toString(),
    permisos,
    empresas_asignadas: organizations,
    empresa_activa: organizations[0] ?? null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
};

// ===============================
// TIPOS COMPATIBLES CON CÓDIGO EXISTENTE
// ===============================

interface EstadoAuth {
  usuario: User | null;
  estaAutenticado: boolean;
  cargando: boolean;
  esAdmin: boolean;
  esSuperuser: boolean;
  permisos: string[];
  estadoSesion: SessionState;
  reconectando: boolean;
  organizaciones: Empresa[];
  empresaActiva: Empresa | null;
}

interface AccionesAuth {
  iniciarSesion: (credenciales: LoginRequest) => Promise<void>;
  cerrarSesion: () => Promise<void>;
  actualizarPerfil: () => Promise<void>;
  editarPerfil: (data: Partial<User>) => Promise<void>;
  tienePermiso: (permiso: string) => boolean;
  tieneAlgunPermiso: (permisos: string[]) => boolean;
  tieneTodosLosPermisos: (permisos: string[]) => boolean;
  verificarSesion: () => Promise<void>;
  manejarReconexion: () => Promise<void>;
  cambiarEmpresa: (empresaId: number) => Promise<void>;
  refrescarPermisos: () => Promise<void>;
}

export type AuthContextType = EstadoAuth & AccionesAuth & {
  // Aliases para compatibilidad
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  isSuperuser: boolean;
  permissions: string[];
  sessionState: SessionState;
  isReconnecting: boolean;
  hasPermission: (permiso: string) => boolean;
  hasAnyPermission: (permisos: string[]) => boolean;
  hasAllPermissions: (permisos: string[]) => boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [estado, setEstado] = useState<EstadoAuth>({
    usuario: null,
    estaAutenticado: false,
    cargando: true,
    esAdmin: false,
    esSuperuser: false,
    permisos: [],
    estadoSesion: SessionState.CHECKING,
    reconectando: false,
    organizaciones: [],
    empresaActiva: null
  });

  // ===============================
  // INICIALIZACIÓN Y LISTENERS
  // ===============================

  useEffect(() => {
    let isMounted = true;

    const inicializarAuth = async () => {
      try {
        const infoUsuario = AuthServiceUAU.getCurrentUserInfo();

        if (infoUsuario.isAuthenticated && infoUsuario.user) {
          cargarUsuarioDelStorage(isMounted, infoUsuario);
        } else {
          if (isMounted) {
            setEstado(prev => ({
              ...prev,
              cargando: false,
              estadoSesion: SessionState.INVALID
            }));
          }
        }
      } catch (error) {
        if (isMounted) {
          setEstado(prev => ({
            ...prev,
            cargando: false,
            estadoSesion: SessionState.INVALID
          }));
        }
      }
    };

    const cargarUsuarioDelStorage = (mounted: boolean, infoUsuario: any) => {
      if (mounted) {
        const usuarioActualizado = AuthServiceUAU.getUser();
        const empresaActiva = AuthServiceUAU.getEmpresaActiva() ?? null;

        // ✅ VERIFICAR SI HAY EMPRESA PENDIENTE DESDE EMAIL
        const empresaIdFromEmail = sessionStorage.getItem('email_redirect_empresa_id');
        let empresaFinal = empresaActiva;

        if (empresaIdFromEmail && usuarioActualizado?.empresas_asignadas) {
          const empresaId = parseInt(empresaIdFromEmail, 10);
          const empresaTarget = usuarioActualizado.empresas_asignadas.find(emp => emp.id === empresaId);

          if (empresaTarget && (!empresaActiva || empresaActiva.id !== empresaId)) {
            // Cambiar a la empresa del email
            AuthServiceUAU.setEmpresaActiva({
              id: empresaTarget.id,
              nombre: empresaTarget.nombre,
              rut: empresaTarget.rut || '',
              es_activa: true
            });
            empresaFinal = empresaTarget;

            // Limpiar sessionStorage
            sessionStorage.removeItem('email_redirect_empresa_id');

            // Disparar evento
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('empresa-changed', {
                detail: { empresaAnterior: empresaActiva, empresaNueva: empresaTarget }
              }));
            }, 100);
          }
        }

        if (!usuarioActualizado) {
          setEstado(prev => ({
            ...prev,
            cargando: false,
            estadoSesion: SessionState.INVALID
          }));
          return;
        }

        if (empresaFinal && usuarioActualizado.empresa_activa?.id !== empresaFinal.id) {
          usuarioActualizado.empresa_activa = empresaFinal;
        }

        setEstado({
          usuario: usuarioActualizado,
          estaAutenticado: true,
          cargando: false,
          esAdmin: usuarioActualizado.tipo_usuario === 'administrador',
          esSuperuser: infoUsuario.isSuperuser,
          permisos: usuarioActualizado.permisos?.map(p => p.codigo) || [],
          estadoSesion: SessionState.VALID,
          reconectando: false,
          organizaciones: usuarioActualizado.empresas_asignadas || [],
          empresaActiva: empresaFinal || usuarioActualizado.empresa_activa
        });
      }
    };

    inicializarAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  // ✅ LISTENER PARA CAMBIOS DE EMPRESA DESDE EMAILS
  useEffect(() => {
    const handleEmpresaChanged = (event: CustomEvent) => {
      // Actualizar empresa activa en el estado
      const empresaActiva = AuthServiceUAU.getEmpresaActiva();

      if (empresaActiva) {
        setEstado(prev => ({
          ...prev,
          empresaActiva
        }));
      }
    };

    // Escuchar evento personalizado
    window.addEventListener('empresa-changed', handleEmpresaChanged as EventListener);

    return () => {
      window.removeEventListener('empresa-changed', handleEmpresaChanged as EventListener);
    };
  }, []);

  // ✅ VERIFICAR EMPRESA PENDIENTE DESPUÉS DE AUTENTICACIÓN
  useEffect(() => {
    // Solo ejecutar si el usuario acaba de autenticarse
    if (estado.estaAutenticado && estado.usuario && !estado.cargando) {
      const empresaIdFromEmail = sessionStorage.getItem('email_redirect_empresa_id');

      if (empresaIdFromEmail) {
        const empresaId = parseInt(empresaIdFromEmail, 10);
        const empresaActual = AuthServiceUAU.getEmpresaActiva();

        if (!empresaActual || empresaActual.id !== empresaId) {
          const empresasDisponibles = estado.usuario.empresas_asignadas || [];
          const empresaTarget = empresasDisponibles.find(emp => emp.id === empresaId);

          if (empresaTarget) {
            AuthServiceUAU.setEmpresaActiva({
              id: empresaTarget.id,
              nombre: empresaTarget.nombre,
              rut: empresaTarget.rut || '',
              es_activa: true
            });

            setEstado(prev => ({
              ...prev,
              empresaActiva: empresaTarget
            }));

            // Limpiar sessionStorage
            sessionStorage.removeItem('email_redirect_empresa_id');

            // Disparar evento
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('empresa-changed', {
                detail: { empresaAnterior: empresaActual, empresaNueva: empresaTarget }
              }));
            }, 100);
          } else {
            console.warn(`⚠️ AuthContext: Empresa ${empresaId} no encontrada post-auth`);
            sessionStorage.removeItem('email_redirect_empresa_id');
          }
        } else {
          sessionStorage.removeItem('email_redirect_empresa_id');
        }
      }
    }
  }, [estado.estaAutenticado, estado.usuario, estado.cargando]);

  // ===============================
  // ACCIONES
  // ===============================

  const iniciarSesion = useCallback(async (credenciales: LoginRequest): Promise<void> => {
    try {
      setEstado(prev => ({
        ...prev,
        cargando: true,
        estadoSesion: SessionState.CHECKING
      }));

      const response = await AuthServiceUAU.login(credenciales);
      const userInfo = response.user;

      // Crear estructura UAU para mapear
      const uauUserData: UAUUserData = {
        user_id: userInfo.id,
        username: userInfo.username,
        email: userInfo.email,
        full_name: userInfo.nombre_completo,
        is_active: userInfo.es_activo,
        user_type: userInfo.tipo_usuario,
        organizations: userInfo.empresas_asignadas?.map((org: any) => ({
          id: org.id,
          nombre: org.nombre || org.name,
          rut: org.rut,
          es_activa: true
        })) || [],
        permissions: (userInfo.permisos_menu || []).reduce((acc: Record<string, boolean>, perm: string) => {
          acc[perm] = true;
          return acc;
        }, {}) || {}
      };

      const usuarioMapeado = mapUAUToRendicionesUser(uauUserData);
      usuarioMapeado.permisos_menu = userInfo.permisos_menu;

      // ESTABLECER TOKEN - CRÍTICO para futuras llamadas API
      AuthServiceUAU.setToken(response.access_token);

      // ⭐ RESETEAR ESTADO DE SESIÓN EXPIRADA
      resetSessionState();

      // GUARDAR EN LOCALSTORAGE
      localStorage.setItem('camping_login_data', JSON.stringify(response));
      localStorage.setItem('camping_user', JSON.stringify(usuarioMapeado));
      localStorage.setItem('camping_permissions', JSON.stringify(uauUserData.permissions));

      // ✅ OBTENER EMPRESA ACTIVA Y VERIFICAR PENDIENTE DESDE EMAIL
      let empresaActiva = AuthServiceUAU.getEmpresaActiva() ?? null;
      let empresaFinal = empresaActiva; // ✅ Declarar fuera del bloque condicional

      // ✅ VERIFICAR SI HAY EMPRESA PENDIENTE DESDE EMAIL (durante login)
      const empresaIdFromEmail = sessionStorage.getItem('email_redirect_empresa_id');

      if (empresaIdFromEmail && uauUserData.organizations) {
        const empresaId = parseInt(empresaIdFromEmail, 10);
        const empresaTarget = uauUserData.organizations.find(emp => emp.id === empresaId);

        if (empresaTarget && (!empresaActiva || empresaActiva.id !== empresaId)) {
          // Cambiar a la empresa del email
          const empresaFormatted = {
            id: empresaTarget.id,
            nombre: empresaTarget.nombre,
            rut: empresaTarget.rut || '',
            es_activa: true
          };

          AuthServiceUAU.setEmpresaActiva(empresaFormatted);
          empresaFinal = empresaFormatted;

          // Limpiar sessionStorage
          sessionStorage.removeItem('email_redirect_empresa_id');
        }
      }

      setEstado({
        usuario: usuarioMapeado,
        estaAutenticado: true,
        cargando: false,
        esAdmin: usuarioMapeado.tipo_usuario === 'administrador',
        esSuperuser: usuarioMapeado.tipo_usuario === 'administrador',
        permisos: usuarioMapeado.permisos.map(p => p.codigo),
        estadoSesion: SessionState.VALID,
        reconectando: false,
        organizaciones: uauUserData.organizations || [],
        empresaActiva: empresaFinal || empresaActiva
      });

      // ✅ DISPARAR EVENTO SI SE CAMBIÓ EMPRESA DURANTE LOGIN
      if (empresaFinal && empresaFinal.id !== empresaActiva?.id) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('empresa-changed', {
            detail: { empresaAnterior: empresaActiva, empresaNueva: empresaFinal }
          }));
        }, 500);
      }

      // Mostrar mensaje de bienvenida
      toastSuccess(`¡Bienvenido ${usuarioMapeado.nombre_completo}!`);

    } catch (error: any) {
      setEstado(prev => ({
        ...prev,
        cargando: false,
        estadoSesion: SessionState.INVALID
      }));

      // ⭐ NO mostrar toast aquí - LoginPage se encarga de eso
      // AuthContext solo relanza el error para que LoginPage lo maneje
      throw error;
    }
  }, []);

  const cerrarSesion = useCallback(async (): Promise<void> => {
    try {
      await AuthServiceUAU.logout();

      setEstado({
        usuario: null,
        estaAutenticado: false,
        cargando: false,
        esAdmin: false,
        esSuperuser: false,
        permisos: [],
        estadoSesion: SessionState.INVALID,
        reconectando: false,
        organizaciones: [],
        empresaActiva: null
      });

      toastSuccess('Sesión cerrada correctamente');
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
    } catch (error) {
      AuthServiceUAU.clearStorageSilently();
      window.location.href = '/login';
    }
  }, []);

  const tienePermiso = useCallback((permiso: string): boolean => {
    return estado.permisos.includes(permiso) || estado.esAdmin;
  }, [estado.permisos, estado.esAdmin]);

  const tieneAlgunPermiso = useCallback((permisos: string[]): boolean => {
    if (estado.esAdmin) return true;
    return permisos.some(permiso => estado.permisos.includes(permiso));
  }, [estado.permisos, estado.esAdmin]);

  const tieneTodosLosPermisos = useCallback((permisos: string[]): boolean => {
    if (estado.esAdmin) return true;
    return permisos.every(permiso => estado.permisos.includes(permiso));
  }, [estado.permisos, estado.esAdmin]);

  const cambiarEmpresa = useCallback(async (empresaId: number): Promise<void> => {
    try {
      await AuthServiceUAU.cambiarEmpresa(empresaId);

      const empresaActiva = AuthServiceUAU.getEmpresaActiva() ?? null;
      const usuario = AuthServiceUAU.getUser();

      setEstado(prev => ({
        ...prev,
        empresaActiva,
        usuario
      }));

      toastSuccess('Empresa cambiada correctamente');

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      toastError(error.message || 'Error al cambiar empresa');
      throw error;
    }
  }, []);

  const refrescarPermisos = useCallback(async (): Promise<void> => {
    try {
      await AuthServiceUAU.refrescarPermisos();

      const usuarioActualizado = AuthServiceUAU.getUser();

      if (usuarioActualizado) {
        setEstado(prev => ({
          ...prev,
          usuario: usuarioActualizado,
          permisos: usuarioActualizado.permisos.map(p => p.codigo),
          esAdmin: usuarioActualizado.tipo_usuario === 'administrador'
        }));
      }
    } catch (error: any) {
      console.error('Error al refrescar permisos:', error);
      throw error;
    }
  }, []);

  // Funciones stub para compatibilidad
  const actualizarPerfil = useCallback(async (): Promise<void> => {}, []);
  const editarPerfil = useCallback(async (_data: Partial<User>): Promise<void> => {}, []);
  const verificarSesion = useCallback(async (): Promise<void> => {}, []);
  const manejarReconexion = useCallback(async (): Promise<void> => {}, []);

  // ===============================
  // CONTEXT VALUE
  // ===============================

  const contextValue: AuthContextType = {
    ...estado,
    iniciarSesion,
    cerrarSesion,
    actualizarPerfil,
    editarPerfil,
    tienePermiso,
    tieneAlgunPermiso,
    tieneTodosLosPermisos,
    verificarSesion,
    manejarReconexion,
    cambiarEmpresa,
    refrescarPermisos,

    // Aliases para compatibilidad
    user: estado.usuario,
    isAuthenticated: estado.estaAutenticado,
    isLoading: estado.cargando,
    isAdmin: estado.esAdmin,
    isSuperuser: estado.esSuperuser,
    permissions: estado.permisos,
    sessionState: estado.estadoSesion,
    isReconnecting: estado.reconectando,
    hasPermission: tienePermiso,
    hasAnyPermission: tieneAlgunPermiso,
    hasAllPermissions: tieneTodosLosPermisos
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// ===============================
// AUTORIDAD ÚNICA PARA SESIÓN EXPIRADA
// ===============================

export class AuthContextManager {
  static handleSessionExpired() {
    localStorage.clear();
    toastError('Sesión expirada. Por favor, inicia sesión nuevamente.');
    setTimeout(() => {
      window.location.href = '/login?expired=true';
    }, 1500);
  }
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export default AuthProvider;
